import { PRECISION } from './constants';
import { Hole } from './hole';
import { Part, SVG_NS, ViewContext } from './part';
import { getState } from './state';

export interface SpokeProps {
  flangeHole: Hole;
  rimHole: Hole;
}

export class Spoke extends Part {
  children: Part[] = [];
  group?: string;

  constructor(
    public id: string,
    public props: SpokeProps
  ) {
    super();
  }

  update(props: Partial<SpokeProps>): void {
    this.props = {
      ...this.props,
      ...props,
    };
  }

  get length(): number {
    const fh = this.props.flangeHole;
    const rh = this.props.rimHole;
    const [xR, yR] = rh.xyMm;
    const [xF, yF] = fh.xyMm;
    const dx = xR - xF;
    const dy = yR - yF;
    const flatLength = Math.sqrt(dx * dx + dy * dy);
    const flangeHoleOffset = fh.centerOffset * (fh.isOnDs ? 1 : -1);
    const rimHoleOffset = rh.centerOffset * (rh.isOnDs ? 1 : -1);
    const latOffset = Math.abs(rimHoleOffset - flangeHoleOffset);
    const latAdjLength = Math.sqrt(latOffset * latOffset + flatLength * flatLength);
    const finalLength = latAdjLength - this.props.flangeHole.props.diameter / 2;
    return finalLength;
  }

  get angleAtRim(): number {
    const fh = this.props.flangeHole;
    const rh = this.props.rimHole;
    const [xR, yR] = rh.xyMm;
    const [xF, yF] = fh.xyMm;
    const dx = xR - xF;
    const dy = yR - yF;
    const flatLength = Math.sqrt(dx * dx + dy * dy);
    const flangeRadius = fh.props.distance;
    const angleDiff = Math.abs(rh.props.angle - fh.props.angle);
    const rimAngleRad = Math.asin(
      (flangeRadius / flatLength) * Math.sin((angleDiff * Math.PI) / 180)
    );
    return Math.abs((rimAngleRad * 180) / Math.PI);
  }

  get bracingAngle(): number {
    const fh = this.props.flangeHole;
    const rh = this.props.rimHole;
    const [xR, yR] = rh.xyMm;
    const [xF, yF] = fh.xyMm;
    const dx = xR - xF;
    const dy = yR - yF;
    const flatLength = Math.sqrt(dx * dx + dy * dy);
    const flangeHoleOffset = fh.centerOffset * (fh.isOnDs ? 1 : -1);
    const rimHoleOffset = rh.centerOffset * (rh.isOnDs ? 1 : -1);
    const latOffset = Math.abs(rimHoleOffset - flangeHoleOffset);
    return (Math.atan(latOffset / flatLength) * 180) / Math.PI;
  }

  hasExtremeAngles(threshold: number): boolean {
    return this.angleAtRim > threshold || this.bracingAngle > threshold;
  }

  render(viewCtx: ViewContext): SVGElement {
    const spokeMarkerSizeRatio = 0.5;
    const group = document.createElementNS(SVG_NS, 'g');
    group.classList.add('spoke');
    if (getState().selectedId === this.id) {
      group.classList.add('selected');
    }
    if (this.props.flangeHole.isOnFarSide(viewCtx.viewSide)) {
      group.classList.add('far-side');
    }
    group.setAttribute('id', this.id);
    const innerGroup = document.createElementNS(SVG_NS, 'g');
    const start = this.props.flangeHole;
    const end = this.props.rimHole;
    const line = document.createElementNS(SVG_NS, 'line');
    line.setAttribute('x1', start.xy(viewCtx)[0].toFixed(PRECISION));
    line.setAttribute('y1', start.xy(viewCtx)[1].toFixed(PRECISION));
    line.setAttribute('x2', end.xy(viewCtx)[0].toFixed(PRECISION));
    line.setAttribute('y2', end.xy(viewCtx)[1].toFixed(PRECISION));
    const startMarker = document.createElementNS(SVG_NS, 'circle');
    startMarker.classList.add('spoke-marker');
    startMarker.setAttribute('cx', start.xy(viewCtx)[0].toFixed(PRECISION));
    startMarker.setAttribute('cy', start.xy(viewCtx)[1].toFixed(PRECISION));
    startMarker.setAttribute(
      'r',
      (start.d(viewCtx) * 0.5 * spokeMarkerSizeRatio).toFixed(PRECISION)
    );
    const endMarker = document.createElementNS(SVG_NS, 'circle');
    endMarker.classList.add('spoke-marker');
    endMarker.setAttribute('cx', end.xy(viewCtx)[0].toFixed(PRECISION));
    endMarker.setAttribute('cy', end.xy(viewCtx)[1].toFixed(PRECISION));
    endMarker.setAttribute('r', (end.d(viewCtx) * 0.5 * spokeMarkerSizeRatio).toFixed(PRECISION));
    if (this.hasExtremeAngles(viewCtx.angleThreshold)) {
      endMarker.classList.add('warning');
    }
    const title = document.createElementNS(SVG_NS, 'title');
    let titleText = `Spoke ${this.id} | Length: ${this.length.toFixed(1)}mm`;
    if (this.group) {
      titleText = titleText.concat(` | Group: ${this.group}`);
    }
    title.textContent = titleText;
    const endMarkerTitle = document.createElementNS(SVG_NS, 'title');
    let endMarkerTitleText = `Hole ${this.props.rimHole.id} | Rim angle: ${this.angleAtRim.toFixed(1)}° | Bracing angle: ${this.bracingAngle.toFixed(1)}°`;
    endMarkerTitle.textContent = endMarkerTitleText;
    innerGroup.appendChild(line);
    innerGroup.appendChild(startMarker);
    innerGroup.appendChild(title);
    endMarker.appendChild(endMarkerTitle);
    if (this.group && viewCtx.schematicView) {
      const labelG = document.createElementNS(SVG_NS, 'g');
      labelG.classList.add('spoke-group-label');
      const [sx, sy] = start.xy(viewCtx);
      const [ex, ey] = end.xy(viewCtx);
      const labelCircle = document.createElementNS(SVG_NS, 'circle');
      labelCircle.classList.add('spoke-group-label-background');
      labelCircle.setAttribute('cx', ((sx + ex) / 2).toFixed(PRECISION));
      labelCircle.setAttribute('cy', ((sy + ey) / 2).toFixed(PRECISION));
      labelCircle.setAttribute('r', '5');
      const labelText = document.createElementNS(SVG_NS, 'text');
      labelText.setAttribute('x', ((sx + ex) / 2).toFixed(PRECISION));
      labelText.setAttribute('y', ((sy + ey) / 2).toFixed(PRECISION));
      labelText.setAttribute('text-anchor', 'middle');
      labelText.textContent = this.group;
      labelG.appendChild(labelCircle);
      labelG.appendChild(labelText);
      innerGroup.appendChild(labelG);
    }
    group.appendChild(innerGroup);
    group.appendChild(endMarker);
    this.addEventHandlers(group);
    return group;
  }

  addEventHandlers(elem: SVGElement): void {
    elem.addEventListener('click', () => {
      const state = getState();
      state.select(state.selectedId === this.id ? undefined : this.id);
    });
    elem.addEventListener('dblclick', () => {
      const state = getState();
      if (state.selectedId === this.id) {
        state.wheel?.removeSpoke(this.id);
        state.select(undefined);
      }
    });
  }
}
